import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardStats, getProjects } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  FolderKanban, 
  Clock, 
  CreditCard, 
  MessageSquare,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClientDashboard() {
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, projectsRes] = await Promise.all([
        getDashboardStats(),
        getProjects()
      ]);
      setStats(statsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const statCards = [
    { label: 'My Projects', value: stats?.total_projects || 0, icon: FolderKanban, color: 'text-[#4A90D9]' },
    { label: 'Awaiting Approval', value: stats?.pending_approvals || 0, icon: Clock, color: 'text-[#F59E0B]' },
    { label: 'Pending Payments', value: stats?.pending_payments || 0, icon: CreditCard, color: 'text-[#EF4444]' },
    { label: 'Unread Messages', value: stats?.recent_messages || 0, icon: MessageSquare, color: 'text-[#38BDF8]' },
  ];

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-slate-400">Welcome to your client portal</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div 
            key={stat.label}
            className="stats-card card-hover animate-fade-in-up opacity-0"
            style={{ animationDelay: `${0.1 * index}s` }}
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-center justify-between mb-3">
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <div className={`value ${stat.color}`}>{stat.value}</div>
            <div className="label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Projects List */}
      <Card className="bg-[#111827] border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <FolderKanban className="w-5 h-5 text-[#4A90D9]" />
            My Projects
          </CardTitle>
          <Link to="/dashboard/projects">
            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {projects.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No projects yet</p>
          ) : (
            <div className="space-y-3">
              {projects.slice(0, 5).map((project) => (
                <Link 
                  key={project.id}
                  to={`/dashboard/projects/${project.id}`}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors block"
                  data-testid={`project-${project.id}`}
                >
                  <div>
                    <p className="text-white font-medium">{project.name}</p>
                    <p className="text-sm text-slate-400">
                      {project.description?.substring(0, 60) || 'No description'}
                      {project.description?.length > 60 ? '...' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`badge-${project.status}`}>
                      {project.status.replace('_', ' ')}
                    </Badge>
                    {project.payment_status !== 'paid' && (
                      <Badge className={`badge-${project.payment_status}`}>
                        {project.payment_status}
                      </Badge>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
